<!-- Grupperet efter status, fejl først. Fejlgruppen og dens delkørsler er udfoldet fra start, så en
     fejlet kørsel viser fejlene uden et klik; "Færdige" er lukket og er primært vejen til payloads. -->
@for (group of groups(); track group.key) {
    <div class="executor-group" [attr.data-group]="group.key">
        <button type="button" class="executor-group-header"
            [attr.aria-expanded]="isGroupExpanded(group.key)"
            (click)="toggleGroup(group.key)">
            <i class="fa-solid chevron"
                [class.fa-chevron-right]="!isGroupExpanded(group.key)"
                [class.fa-chevron-down]="isGroupExpanded(group.key)"
                aria-hidden="true"></i>
            <span class="executor-group-label">{{ group.label }}</span>
            <span class="badge" [class]="'bg-' + group.variant">{{ group.executers.length }}</span>
        </button>

        @if (isGroupExpanded(group.key)) {
            @for (executer of group.executers; track executer.id) {
                <div class="executor"
                    [class.is-open]="isExecutorExpanded(executer)"
                    [class.is-static]="!canToggle(executer)">

                    <!-- Header is a div, not a button: the payload action is its own button and
                         nesting one inside another is invalid. -->
                    <div class="executor-header">

                        <!-- Only a chunk that has something to show is a button. A chunk with no
                             message and no loglinjer would open onto nothing, and offering the
                             control anyway reads as "broken", not as "empty". The summary itself is
                             identical either way, so it lives in one template. -->
                        @if (canToggle(executer)) {
                            <button type="button" class="executor-toggle"
                                [attr.aria-expanded]="isExecutorExpanded(executer)"
                                (click)="toggleExecutor(executer)">
                                <i class="fa-solid chevron"
                                    [class.fa-chevron-right]="!isExecutorExpanded(executer)"
                                    [class.fa-chevron-down]="isExecutorExpanded(executer)"
                                    aria-hidden="true"></i>
                                <ng-container
                                    *ngTemplateOutlet="summary; context: { executer: executer }" />
                            </button>
                        } @else {
                            <div class="executor-toggle no-toggle">
                                <!-- Lone chunk: always open, so the stack icon stands in for a
                                     chevron. Nothing to show: an empty slot, keeping the columns
                                     aligned with the rows that do have one. -->
                                @if (isExecutorExpanded(executer)) {
                                    <i class="fa-solid fa-layer-group chevron" aria-hidden="true"></i>
                                } @else {
                                    <span class="chevron" aria-hidden="true"></span>
                                }
                                <ng-container
                                    *ngTemplateOutlet="summary; context: { executer: executer }" />
                            </div>
                        }

                        @if (executer.hasPayload) {
                            <button type="button" class="btn btn-sm btn-outline-primary executor-payload"
                                title="Hent payload for denne delkørsel"
                                (click)="downloadPayload.emit(executer)">
                                <i class="fa-solid fa-file-arrow-down me-1" aria-hidden="true"></i> Payload
                            </button>
                        }
                    </div>

                    @if (isExecutorExpanded(executer)) {
                        <div class="executor-body">
                            @if (executer.message) {
                                <div class="executor-message">
                                    <i class="fa-solid fa-circle-exclamation" aria-hidden="true"></i>
                                    <app-expandable-message [text]="executer.message" />
                                </div>
                            }

                            <!-- Phase strip: the chunk's QueueTimeTracker rows with the time each
                                 phase took. Developer detail, so admin-only. -->
                            @if (developerInfo() && executer.phases.length > 0) {
                                <ol class="executor-phases" aria-label="Faser for delkørslen">
                                    @for (phase of phasesFor(executer); track phase.start) {
                                        <li class="executor-phase" [class.is-terminal]="phase.terminal">
                                            <span class="phase-label">{{ phase.status | queueStatusLabel }}</span>
                                            <span class="phase-time tabular">{{ phase.start | date:'HH:mm:ss' }}</span>
                                            @if (phase.durationLabel) {
                                                <span class="phase-duration tabular">{{ phase.durationLabel }}</span>
                                            }
                                        </li>
                                    }
                                </ol>
                            }

                            @for (entry of logsFor(executer); track entry.id) {
                                <app-queue-log-row [entry]="entry" variant="executor" />
                            }

                            <!-- Ingen tom-tilstand her: en delkørsel uden indhold kan slet ikke
                                 åbnes, så dette markup nås kun med noget at vise. -->
                        </div>
                    }
                </div>
            }
        }
    </div>
}

<ng-template #summary let-executer="executer">
    <span class="executor-index tabular">
        Delkørsel {{ executer.current }} / {{ executer.total }}
    </span>

    <span class="executor-table font-monospace">{{ executer.dbTableFullName }}</span>

    <!-- -1 means the source never counted the rows, which is not the same as 0. -->
    <span class="executor-rows tabular">
        {{ executer.itemsInData >= 0 ? executer.itemsInData + ' rk.' : '' }}
    </span>

    <!-- Pickup time and total duration — admin-only, and blank for kørsler older than the
         attributed tracker rows (2026-09), where neither is knowable. -->
    @if (developerInfo() && executer.startedAt) {
        <span class="executor-timing tabular" title="Startet · varighed">
            {{ executer.startedAt | date:'HH:mm:ss' }} · {{ durationLabelFor(executer) }}
        </span>
    }
</ng-template>
